import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

import { LoadingSpinner } from 'brand-guidelines/Loading-styled-components';
import { requestUserAndPatient } from 'connect/actions';
import { selectDataLoadingUser, selectDataPatient } from 'connect/selectors';
import { useCurrentCheckoutStep } from 'pages/checkout-v3/hooks/checkoutStepHooks';

import { stepComponents } from 'pages/checkout-v3/steps/stepComponents';
import { CheckoutStepDefinition } from 'pages/checkout-v3/steps/types';
import { usePrevious } from 'utils/customHooks/usePrevious';
import { useLogger } from 'utils/Logger';

export const CurrentStep = () => {
  const dispatch = useDispatch();
  const { url: rawUrl, path } = useRouteMatch();
  const url = rawUrl.replace(/\/$/, '');

  const logger = useLogger();
  const [isLoading, setLoading] = useState(true);

  const { currentStep, currentStepIndex, applicableStepDefinitions } = useCurrentCheckoutStep();
  const previousStep = usePrevious<CheckoutStepDefinition | null>(currentStep);
  const isLoadingLogin = useSelector(selectDataLoadingUser);
  const previousIsLoadingLogin = usePrevious(isLoadingLogin);

  const patient = useSelector(selectDataPatient);

  useEffect(() => {
    if (previousStep?.id !== currentStep?.id || !patient?.id) {
      // Makes sure the user and patient are loaded when a step changes
      dispatch(requestUserAndPatient());
    }
  }, [dispatch, patient?.id, currentStep?.id, previousStep?.id]);

  useEffect(() => {
    // Only setloading to false when the login was previously loading
    // And is no longer loading. So we wait for the login to start loading.
    // This prevents certain steps from being marked as inapplicable
    // before the user has loaded.
    // If we didn't do this, the user would always be redirected to the
    // first step when entering the checkout directly via URL.
    if (previousIsLoadingLogin && !isLoadingLogin) {
      setLoading(false);
    }
  }, [isLoadingLogin, previousIsLoadingLogin]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Slice to include up to the next step
  const validStepDefinitions = applicableStepDefinitions.slice(0, currentStepIndex + 2);

  return (
    <Switch>
      {validStepDefinitions.map((stepDefinition) => {
        const StepComponent = stepComponents[stepDefinition.id];

        // Should only happen in development
        if (!StepComponent) {
          logger.error('Step component not found for step', { stepDefinition });
          return null;
        }

        return (
          <Route key={stepDefinition.id} path={`${path}/${stepDefinition.path}`}>
            <StepComponent />
          </Route>
        );
      })}
      <Route>
        <Redirect to={`${url}/${currentStep?.path}`} />
      </Route>
    </Switch>
  );
};
